// Чтение карты доступности в компонентах.
//
// Разделение с `providers/module-availability-provider.tsx` строгое: там —
// ЕДИНСТВЕННЫЙ писатель (запрос, снапшот, реакция на смену аккаунта), здесь —
// только читатель. Второй писатель означал бы вторую гонку за одну карту.
//
// Хук ровно один и отдаёт карту целиком. Обёрток «состояние одного модуля» тут
// нет намеренно: потребители уже держат `view` (панель инструментов, навигация,
// палитра) и зовут чистые функции из `lib/module-availability/resolve` — набор
// хуков-однострочников на каждый вопрос был бы мёртвым кодом и вторым API к
// одной и той же карте.

import { useMemo } from "react";
import { useCurrentUser } from "@/hooks/use-session";
import {
	type ModuleAvailabilityView,
	UNKNOWN_AVAILABILITY_VIEW,
} from "@/lib/module-availability/resolve";
import { useModuleAvailabilityStore } from "@/lib/module-availability/store";

/**
 * Текущая карта доступности.
 *
 * Проверка владельца — структурная, а не эффектом: пока карта в сторе принадлежит
 * другому пользователю (или пользователь ещё неизвестен), наружу отдаётся
 * «неизвестно». Так смена аккаунта в одной вкладке не может показать чужие
 * разделы даже на один кадр — сбрасывать нечего, сравнение уже не сходится.
 */
export function useModuleAvailability(): ModuleAvailabilityView {
	const userId = useCurrentUser()?.id ?? null;
	const source = useModuleAvailabilityStore((s) => s.source);
	const byId = useModuleAvailabilityStore((s) => s.byId);
	const ownerUserId = useModuleAvailabilityStore((s) => s.ownerUserId);

	return useMemo(() => {
		if (!userId || !ownerUserId || ownerUserId !== userId) return UNKNOWN_AVAILABILITY_VIEW;
		return { source, byId };
	}, [userId, ownerUserId, source, byId]);
}
